import { NO_TIMEOUT } from '$extensionSrc/consts/stageMachineConsts'
import { sendAlertNotification } from '$extensionSrc/utils/communication'
import {
  InstagramRecommendationKey,
  PLATFORM,
  StageType,
} from '$extensionSrc/utils/enums'
import { _debug } from '$extensionSrc/utils/logging'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { INSTAGRAM_AUTOMATION_STAGE_STATUSES } from './instagramAutomationStageStatuses'
import * as instagramUrls from './instagramUrls'
import { StageDef } from '../automationMachineInterfaces'
import { throwIfGlobalInterruptRequested } from '../globalInterrupt'
import {
  getWaitForElementToAppearPromise,
  wait,
} from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveAttribute,
  getCaseInsensitiveString,
  getCaseInsensitiveXPathTextSelector,
} from '../interruptibleXpath'
import { DateFilter, DateFilterDate } from '../machineInterfaces'
import { setSelectElement } from '../reactInputElementUtils'
import { getElement, getOptionalElement } from '../utils'

export enum ACTION {
  ARCHIVE = 'ARCHIVE',
  DELETE = 'DELETE',
}

export enum CONTENT {
  POSTS = 'POSTS',
  REELS = 'REELS',
}

const INSTAGRAM_SELECT_LIMIT = 100 // Instagram only allows you to select 100 posts at a time

const NO_MORE_RESULTS_MESSAGE = 'No results'

const contentToUrlMap = {
  [CONTENT.POSTS]: instagramUrls.yourActivityPostsUrl,
  [CONTENT.REELS]: instagramUrls.yourActivityReelsUrl,
}

const noContentMessageMap = {
  [CONTENT.POSTS]: 'You haven’t posted anything',
  [CONTENT.REELS]: 'You haven’t made any reels',
}

function isValidAction(action: ACTION, content: CONTENT) {
  // Reels cannot be archived, only deleted
  if (content === CONTENT.REELS && action === ACTION.ARCHIVE) {
    return false
  }

  return true
}

function wrapWithDebugAndRetry<T>(
  asyncFn: () => Promise<T>,
  onError: () => Promise<any>,
): Promise<T> {
  return asyncFn().catch(async () => {
    _debug(
      `wrap debug retry: asyncFn ${asyncFn.name} failed`,
      'instagramYourContentUtils',
      'd',
    )
    await onError()
    _debug(
      `wrap debug retry: ran onError fn ${onError.name}; now retrying`,
      'instagramYourContentUtils',
      'd',
    )
    return asyncFn()
  })
}

export async function openSortAndFilterModal(): Promise<HTMLDivElement> {
  // Click the "Sort & filter" button to open the modal
  _debug(
    'open sort and filter modal: finding button',
    'instagramYourContentUtils',
    'd',
  )
  const sortFilterButtonEl = await getElement<HTMLDivElement>(
    document,
    `//div[@role="button" and ${getCaseInsensitiveXPathTextSelector('aria-label', 'sort & filter')}]`,
  )
  sortFilterButtonEl.click()
  _debug(
    'open sort and filter modal: button clicked',
    'instagramYourContentUtils',
    'd',
  )

  // Wait for the modal to open
  _debug(
    'open sort and filter modal: waiting for modal',
    'instagramYourContentUtils',
    'd',
  )
  const modalEl = await getElement<HTMLDivElement>(
    document,
    `//div[@role="dialog" and count(descendant::span[${getCaseInsensitiveString('text()')}="sort & filter"]) > 0]`,
  )
  _debug(
    'open sort and filter modal: modal opened',
    'instagramYourContentUtils',
    'd',
  )

  return modalEl
}

export async function setDateInModal(
  modal: HTMLDivElement,
  dateLabel: string,
  date: DateFilterDate,
): Promise<void> {
  _debug(
    `set date in modal: setting ${dateLabel}`,
    'instagramYourContentUtils',
    'd',
  )

  // Find the date label section (either "Start Date" or "End Date")
  const dateSectionEl = await getElement<HTMLElement>(
    modal,
    `./descendant::span[${getCaseInsensitiveString('text()')}="${dateLabel}"]`,
  )

  // Find parent container of date select options (this container is sibling of label)
  // In particular, don't want to pickup the other date sections selectors
  const dateContainerEl = await getElement<HTMLElement>(
    dateSectionEl,
    `./following::*[
      count(descendant::select[contains(${getCaseInsensitiveAttribute('title')}, "month")]) = 1 and
      count(descendant::select[contains(${getCaseInsensitiveAttribute('title')}, "day")]) = 1
    ]`,
  )

  async function setField(fieldName: string, value: string): Promise<void> {
    const element = await getElement<HTMLSelectElement>(
      dateContainerEl,
      `.//select[contains(${getCaseInsensitiveAttribute('title')}, "${fieldName.toLowerCase()}")]`,
    )
    // element.value = value
    // element.dispatchEvent(new Event('change', { bubbles: true }))
    setSelectElement(element, value)
    _debug(
      {
        msg: `set date in modal: ${dateLabel} ${fieldName} set to ${value}`,
        element,
      },
      'instagramYourContentUtils',
      'd',
    )
  }

  await setField('month', date.month.toString(10))
  await setField('day', date.day.toString(10))
  await setField('year', date.year.toString(10))
}

export async function applyFilterModal(modal: HTMLDivElement): Promise<void> {
  _debug(
    'apply filter modal: finding apply button',
    'instagramYourContentUtils',
    'd',
  )

  // Wait a moment for the dates to be set before clicking apply
  await wait(500)

  // Find and click the Apply button
  const applyButtonEl = await getElement<HTMLDivElement>(
    modal,
    `.//div[@role="button" and ${getCaseInsensitiveXPathTextSelector('aria-label', 'apply')} and not(@disabled) and not(@aria-disabled="true")]`,
  )
  applyButtonEl.click()
  _debug(
    'apply filter modal: apply button clicked',
    'instagramYourContentUtils',
    'd',
  )

  // Wait for the modal to close
  await wait(1000)
  _debug('apply filter modal: modal closed', 'instagramYourContentUtils', 'd')
}

async function applyDateFilters(dateFilter: DateFilter): Promise<void> {
  _debug('apply date filters: opening modal', 'instagramYourContentUtils', 'd')
  const modal = await openSortAndFilterModal()

  // Wait a beat; select elements sometimes take a moment to become interactive
  await wait(500)

  if (dateFilter.startDate) {
    _debug(
      'apply date filters: setting start date',
      'instagramYourContentUtils',
      'd',
    )
    await setDateInModal(modal, 'start date', dateFilter.startDate)
  }

  if (dateFilter.endDate) {
    _debug(
      'apply date filters: setting end date',
      'instagramYourContentUtils',
      'd',
    )
    await setDateInModal(modal, 'end date', dateFilter.endDate)
  }

  _debug(
    'apply date filters: applying filters',
    'instagramYourContentUtils',
    'd',
  )
  await applyFilterModal(modal)
  _debug(
    'apply date filters: filters applied',
    'instagramYourContentUtils',
    'd',
  )
}

async function enterSelectionMode(): Promise<void> {
  // After the page has loaded, look for the blue "Select" button and click
  // on it. This makes the "Your activity" page enter selection mode, where
  // individual posts can be checked off.
  _debug('enter selection mode: click button', 'instagramYourContentUtils', 'd')
  const selectButtonEl = await getElement<HTMLSpanElement>(
    document,
    `//div/*[${getCaseInsensitiveString('text()')}="select"]`,
  )
  selectButtonEl.click()

  // Make sure we've actually switched to select mode
  _debug(
    'enter selection mode: wait for checkbox to appear',
    'instagramYourContentUtils',
    'd',
  )
  await getElement<HTMLDivElement>(
    document,
    `//div[` +
      `  @role="button" and ` +
      `  ${getCaseInsensitiveXPathTextSelector('aria-label', 'toggle checkbox')}` +
      `]`,
  )
  _debug(
    'enter selection mode: observed checkbox',
    'instagramYourContentUtils',
    'd',
  )
}

async function getNumberSelected(): Promise<number> {
  _debug(
    'get number selected: find status element',
    'instagramYourContentUtils',
    'd',
  )
  const statusEl = await getElement<HTMLSpanElement>(
    document,
    '//span[contains(text(), " selected")]',
  )
  _debug(
    'get number selected: parsing to get num selected',
    'instagramYourContentUtils',
    'd',
  )
  return parseInt(statusEl.innerText.replace(/ selected/, ''), 10)
}

function getAvailablePosts(): HTMLDivElement[] {
  const checkboxEls = evaluateXpath<HTMLDivElement>(
    document,
    '//div[' +
      '  @role="button" and ' +
      `  ${getCaseInsensitiveXPathTextSelector('aria-label', 'toggle checkbox')}` +
      ']',
  )
  _debug(
    `get available posts: found ${checkboxEls.length} checkbox elements`,
    'instagramYourContentUtils',
    'd',
  )
  return checkboxEls
}

async function scrollToBottomOrLimit(limit?: number): Promise<void> {
  // collectionEl is the main scrollable container holding all of the posts and
  // their checkboxes
  const collectionEl = await getElement<HTMLDivElement>(
    document,
    '//div[@data-bloks-name="bk.components.Collection"]',
  )

  // The scrolly pane will change size depending on the user's screen height.
  // This ensures we scroll down enough but not too much.
  const scrollAmount = collectionEl.getBoundingClientRect().height

  // This code is borrowed from fbAlbumPhotoViewUtils and modified to work here
  let numTryNoChange = 0
  let prevScrollTop = collectionEl.scrollTop

  // Scroll down 1000px and keep doing so every 500ms until collectionEl's
  // scroll top measurement stops changing. This means there are no more posts
  // to load.
  while (numTryNoChange < 6) {
    // Start off with a pause before attempting to scroll
    await wait(1000)

    // Scroll
    collectionEl.scrollBy(0, scrollAmount)

    // See if the scroll loaded any more posts
    if (collectionEl.scrollTop === prevScrollTop) {
      numTryNoChange += 1
    } else {
      numTryNoChange = 0
    }

    const numAvailablePosts = getAvailablePosts().length

    if (limit !== undefined && numAvailablePosts >= limit) {
      return
    }
    prevScrollTop = collectionEl.scrollTop

    throwIfGlobalInterruptRequested()
  }
}

async function waitForNumberSelectedToUpdate(
  currentNumberSelected: number,
): Promise<number> {
  let numberSelected = currentNumberSelected
  let i = 0
  while (numberSelected === currentNumberSelected) {
    await wait(1)
    numberSelected = await getNumberSelected()
    i += 1
    if (i === 5000) {
      throw new Error(
        'waitForNumberSelectedToUpdate: No update after ~5 seconds',
      )
    }
  }
  return numberSelected
}

async function getResultFromConfirmationMessage(
  action: ACTION,
  numberPostsSelected: number,
  isDry: boolean,
): Promise<number> {
  // Wait for Instagram's confirmation message (in a black bar at the
  // bottom of the page) to appear
  const actionText = action === ACTION.DELETE ? 'deleted' : 'archived'

  let numberOfItems = numberPostsSelected
  if (!isDry) {
    const confirmationEl = await getOptionalElement<HTMLButtonElement>(
      document,
      `//p[contains(text(), "You ${actionText}")]`,
    )

    // And parse the number of posts deleted or archived from the message
    if (confirmationEl) {
      numberOfItems = parseInt(
        confirmationEl.innerText.replace(`You ${actionText} `, ''),
        10,
      )
    }
  }

  return numberOfItems
}

export function generateContentUpdateStageDefs(
  action: ACTION,
  content: CONTENT[],
): StageDef[] {
  const updateStageDefs: StageDef[] = [
    {
      // Instagram doesn't reset the post selection widget if you back out of
      // deleting posts on yourActivityPhotosAndVideosUrl. This means a failed or
      // canceled automation could leave the widget in an unexpected state.
      // Navigating to baseUrl and then to yourActivityPhotosAndVideosUrl ensures
      // that the widget is reset and ready for the automation.
      type: StageType.ACTION,
      key: `INIT_INSTAGRAM_${action}_${content}`,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
      url: instagramUrls.baseUrl,
      action: async (_machineContext, userContext) => {
        const { updateStatisticsLastCount } = userContext
        const recommendationKey =
          action === ACTION.DELETE
            ? InstagramRecommendationKey.INSTAGRAM_DELETE_YOUR_POSTS
            : InstagramRecommendationKey.INSTAGRAM_ARCHIVE_POSTS

        updateStatisticsLastCount(recommendationKey, 0)
      },
    },
    ...content
      .filter((contentType) => isValidAction(action, contentType))
      .map(
        (contentType: CONTENT): StageDef => ({
          type: StageType.ACTION,
          key: `INSTAGRAM_${action}_${contentType}`,
          status:
            action === ACTION.DELETE
              ? INSTAGRAM_AUTOMATION_STAGE_STATUSES.DELETING_YOUR_POSTS
              : INSTAGRAM_AUTOMATION_STAGE_STATUSES.ARCHIVING_POSTS,
          url: contentToUrlMap[contentType],
          timeout: NO_TIMEOUT,
          action: async (
            _machineContext,
            userContext,
            additionalMachineData,
          ) => {
            const {
              incrementTotalCount,
              updateStatisticsLastCount,
              recommendationProgress,
            } = userContext
            const {
              inputData: { dateFilter },
            } = additionalMachineData
            const recommendationKey =
              action === ACTION.DELETE
                ? InstagramRecommendationKey.INSTAGRAM_DELETE_YOUR_POSTS
                : InstagramRecommendationKey.INSTAGRAM_ARCHIVE_POSTS

            let totalProcessedThisTime =
              recommendationProgress?.[recommendationKey].statistics
                ?.lastUpdatedCount || 0

            let numRoundsNoPostsUpdated = 0 // this is used as a stopgap to ensure we don't loop infinitely

            // Apply date filters if provided
            if (dateFilter) {
              _debug(
                `applying date filters before selection: ${JSON.stringify(dateFilter)}`,
                'instagramYourContentUtils',
                'd',
              )
              await applyDateFilters(dateFilter)
              _debug(
                'date filters applied successfully',
                'instagramYourContentUtils',
                'd',
              )
            }

            while (numRoundsNoPostsUpdated < 5) {
              // First, make sure the user has some posts or reels to delete
              const noContentMessage = noContentMessageMap[contentType]

              if (noContentMessage) {
                const youHaventPostedAnythingEl =
                  await getWaitForElementToAppearPromise<HTMLDivElement>(
                    document,
                    (node) =>
                      evaluateXpath<HTMLDivElement>(
                        node,
                        `//div[@aria-label="${noContentMessage}"` +
                          `or @aria-label="${NO_MORE_RESULTS_MESSAGE}"]`,
                      )[0],
                    1000,
                    // Don't throw if we can't find it, since this just means the user
                    // has no posts or reels
                  )

                if (youHaventPostedAnythingEl) {
                  // If there aren't any posts, we're done
                  return
                }
              }

              await enterSelectionMode()

              // Scroll to the bottom of the posts
              _debug(
                'scrolling to bottom or first 100',
                'instagramYourContentUtils',
                'd',
              )
              await wait(500)
              await scrollToBottomOrLimit(INSTAGRAM_SELECT_LIMIT)
              _debug('scrolling completed', 'instagramYourContentUtils', 'd')

              // Click on each post's checkbox to select it for deletion
              let numberSelected = await wrapWithDebugAndRetry(
                getNumberSelected,
                enterSelectionMode,
              )
              const checkboxEls = getAvailablePosts()

              remoteLog.debug(
                `INSTAGRAM_${action}_${contentType}: found ${checkboxEls?.length} posts`,
              )

              const checkboxElsToSelect = checkboxEls.slice(
                0,
                INSTAGRAM_SELECT_LIMIT,
              )
              for (const checkboxEl of checkboxElsToSelect) {
                _debug('clicking checkbox', 'instagramYourContentUtils', 'd')
                checkboxEl.click()
                _debug(
                  'awaiting number has updated',
                  'instagramYourContentUtils',
                  'd',
                )
                numberSelected =
                  await waitForNumberSelectedToUpdate(numberSelected)
                _debug('number has updated', 'instagramYourContentUtils', 'd')
              }

              // Lower case, since we do a case insensitive search
              const buttonText = action === ACTION.DELETE ? 'delete' : 'archive'
              // Click on the red "Delete" or "Archive" button. This button opens up a dialog
              // confirming deletion.
              _debug(
                'find first action button',
                'instagramYourContentUtils',
                'd',
              )
              const firstActionButtonEl = await getElement<HTMLSpanElement>(
                document,
                `//div[` +
                  `  @role="button" and` +
                  `  ${getCaseInsensitiveXPathTextSelector('aria-label', buttonText)} and` +
                  `  count(./descendant::*[@role="button"]) = 0` +
                  `]//span`,
              )
              firstActionButtonEl.click()
              _debug(
                'clicked first action button',
                'instagramYourContentUtils',
                'd',
              )

              // Click on the second "Delete" or "Archive" button in the dialog to delete the
              // selected posts
              _debug(
                'find second action button',
                'instagramYourContentUtils',
                'd',
              )
              const secondActionButtonEl = await getElement<HTMLButtonElement>(
                document,
                `//div[@role="dialog"]` +
                  `//div[${getCaseInsensitiveString('text()')}="${buttonText}"]/..`,
              )

              if (!userContext.isDry) {
                secondActionButtonEl.click()
                _debug(
                  'clicked second action button',
                  'instagramYourContentUtils',
                  'd',
                )
              } else {
                const cancelButtonEl = await getElement<HTMLButtonElement>(
                  document,
                  `//div[@role="dialog"]` +
                    `//div[${getCaseInsensitiveString('text()')}="cancel"]/..`,
                )
                cancelButtonEl.click()
                _debug('cancelled (dry mode)', 'instagramYourContentUtils', 'd')
              }

              const numUpdated = await getResultFromConfirmationMessage(
                action,
                numberSelected,
                userContext.isDry,
              )

              if (numUpdated === 0) {
                numRoundsNoPostsUpdated += 1
              } else {
                numRoundsNoPostsUpdated = 0
              }

              _debug(
                `number of items updated: ${numUpdated}`,
                'instagramYourContentUtils',
                'd',
              )
              await incrementTotalCount(recommendationKey, numUpdated)
              await updateStatisticsLastCount(
                recommendationKey,
                totalProcessedThisTime + numUpdated,
              )
              totalProcessedThisTime += numUpdated

              if (userContext.isDry) {
                // We can only "delete" one select limit at a time in dry mode
                break
              }
            }
          },
        }),
      ),
    {
      type: StageType.ACTION,
      key: `FINISH_INSTAGRAM_${action}`,
      status: INSTAGRAM_AUTOMATION_STAGE_STATUSES.CHANGING_PAGES,
      url: instagramUrls.baseUrl,
      action: async (machineContext, userContext) => {
        const { recommendationProgress } = userContext

        const recommendationKey =
          action === ACTION.DELETE
            ? InstagramRecommendationKey.INSTAGRAM_DELETE_YOUR_POSTS
            : InstagramRecommendationKey.INSTAGRAM_ARCHIVE_POSTS

        const numberOfItems =
          recommendationProgress?.[recommendationKey].statistics
            ?.lastUpdatedCount || 0

        const actionText = action === ACTION.DELETE ? 'deleted' : 'archived'

        const notificationContent = `${numberOfItems ? `${numberOfItems} ` : ''}Instagram ${numberOfItems === 1 ? 'post has' : 'posts have'} been ${actionText}.`
        sendAlertNotification(
          PLATFORM.INSTAGRAM,
          `Instagram content ${action === ACTION.DELETE ? 'deletion' : 'archiving'} complete`,
          notificationContent,
        )
      },
    },
  ]

  return updateStageDefs
}
